import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

const M = process.env.PUBLIC_URL;
const API = process.env.REACT_APP_BACKEND_URL;

export const Lightbox = ({ photos, index, onClose, onMove }) => {
  const photo = photos[index];
  const step = useCallback((d) => onMove((index + d + photos.length) % photos.length), [index, photos.length, onMove]);

  useEffect(() => {
    if (!photo) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [photo, onClose, step]);

  return createPortal(
    <AnimatePresence>
      {photo && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex flex-col" onClick={onClose} data-testid="lightbox">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-cream" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm text-cream/70" data-testid="lightbox-counter">{index + 1} / {photos.length}</p>
            <div className="flex items-center gap-2">
              <a href={`${API}/api/photos/${photo.n}/original`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream/20 text-xs font-semibold hover:bg-cream hover:text-ink transition-colors duration-200" data-testid="lightbox-download">
                <Download size={14} /> <span className="hidden sm:inline">Download full-res original</span><span className="sm:hidden">Full-res</span>
              </a>
              <button onClick={onClose} aria-label="Close" className="grid place-items-center h-10 w-10 rounded-full bg-cream/10 hover:bg-cream hover:text-ink transition-colors duration-200" data-testid="lightbox-close"><X size={18} /></button>
            </div>
          </div>
          <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-20 pb-6">
            <motion.img
              key={photo.n}
              src={`${M}/media/gallery/web/${photo.n}.jpg`}
              alt={`Grimsby Turkey Trot photo ${photo.n}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-h-full max-w-full object-contain rounded-xl shadow-2xl select-none"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            <button onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full bg-cream/10 text-cream hover:bg-cream hover:text-ink transition-colors duration-200" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>
            <button onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full bg-cream/10 text-cream hover:bg-cream hover:text-ink transition-colors duration-200" data-testid="lightbox-next"><ChevronRight size={22} /></button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
